import { createFileRoute } from "@tanstack/react-router";
import { Alert } from "antd";
import { useResendVerifyEmailMutation, useVerifyUserEmailLinkQuery } from "api/User/hooks.ts";
import { ActivateForm } from "components/Forms/ActivateForm/ActivateForm.tsx";
import { AuthPageContainer } from "components/UI/AuthPageContainer/AuthPageContainer.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import VerifyEmailResendRequest = App.Http.Requests.User.VerifyEmailResendRequest;

export const Route = createFileRoute("/auth/_layout/activate/$userId/$emailHash/")({
    component: ActivatePage,
});

function ActivatePage() {
    const { emailHash, userId } = Route.useParams();
    const [verifyEmailResent, setVerifyEmailResent] = useState(false);
    const [invalidToken, setInvalidToken] = useState(true);
    const { t: tAuth } = usePaldockTranslation("auth.activate");
    const { t: tRegistration } = usePaldockTranslation("auth.registration");
    const url = new URL(window.location.href);
    const expires = url.searchParams.get("expires");
    const signature = url.searchParams.get("signature");
    const resendVerifyEmailRequest: VerifyEmailResendRequest = {
        id: userId,
        return_url: `${import.meta.env.VITE_APP_URL}/auth/activate/{id}/{hash}`,
    };
    const resendVerifyEmailMutation = useResendVerifyEmailMutation({
        onSuccess: () => {
            toast.success(tAuth("messages.verify_email_resent"));
            setVerifyEmailResent(true);
        },
    });
    const verifyRequest = {
        id: userId,
        hash: emailHash,
        expires: expires,
        signature: signature,
    };
    const verifyEmailLinkQuery = useVerifyUserEmailLinkQuery(verifyRequest);

    useEffect(() => {
        if (verifyEmailLinkQuery.isSuccess) {
            setInvalidToken(false);
        } else if (verifyEmailLinkQuery.isError) {
            setInvalidToken(true);
        }
    }, [verifyEmailLinkQuery.isSuccess, verifyEmailLinkQuery.isError, verifyEmailLinkQuery.data]);

    const verifyAndResendEmail = () => {
        return verifyEmailResent ? (
            <Alert
                type="success"
                message={tAuth("messages.verify_email_resent")}
                showIcon
                style={{ boxShadow: "0px 3px 6px -4px #0000001F", border: "none" }}
            />
        ) : (
            <Alert
                type="error"
                message={tAuth("messages.invalid_token")}
                description={
                    <a
                        onClick={() => resendVerifyEmailMutation.mutate(resendVerifyEmailRequest)}
                        style={{ color: "#171717", textDecoration: "underline" }}
                        className="hover:!no-underline hover:!text-[#171717]"
                    >
                        {tAuth("actions.resend_link")}
                    </a>
                }
                showIcon
                style={{ boxShadow: "0px 3px 6px -4px #0000001F", border: "none" }}
            />
        );
    };

    return (
        <>
            {!verifyEmailLinkQuery.isLoading && (
                <AuthPageContainer title={tRegistration("title")}>
                    {resendVerifyEmailMutation.isError && (
                        <div>{(resendVerifyEmailMutation.error as Error).message}</div>
                    )}
                    {invalidToken ? verifyAndResendEmail() : <ActivateForm userId={userId} emailHash={emailHash} />}
                </AuthPageContainer>
            )}
        </>
    );
}
